/**
 * @fileoverview EditTransactionForm component for the edit modal.
 * Handles all transaction types using edit state.
 * Layout matches TransactionForm for consistency.
 */

import BasicFieldsRow from '../TransactionForm/BasicFieldsRow';
import AdjustmentFields from '../TransactionForm/AdjustmentFields';
import CategoryList from '../TransactionForm/CategoryList';
import styles from './EditTransactionForm.module.css';

/**
 * EditTransactionForm Component
 *
 * Form for editing transactions within a modal.
 * Handles type changes, field updates, and category management.
 * Uses same layout as TransactionForm for consistency.
 */
function EditTransactionForm({ transactionHook, budgetStartMonth, singleBankMode }) {
    // Handle field changes - maps to edit state setters
    const handleFieldChange = (fieldName, value) => {
        switch (fieldName) {
            case 'amount':
                transactionHook.setEditAmount(value);
                // For Transfer, auto-fill the category amount
                if (transactionHook.editTransactionType === 'Transfer' && transactionHook.editTransactionCategories.length === 1) {
                    transactionHook.updateEditTransactionCategory(0, 'amount', value);
                }
                break;
            case 'description':
                transactionHook.setEditDescription(value);
                break;
            case 'date':
                transactionHook.setEditDate(value);
                break;
            case 'currentUserKey':
                transactionHook.setEditCurrentUserKey(value);
                break;
            case 'paidDirectly':
                transactionHook.setEditPaidDirectly(value);
                break;
            case 'adjustmentFromCategory':
                transactionHook.setEditAdjustmentFromCategory(value);
                break;
            case 'adjustmentToCategory':
                transactionHook.setEditAdjustmentToCategory(value);
                break;
            default:
                break;
        }
    };

    // Handle category changes - maps to edit category functions
    const handleCategoryChange = (action, index, field, value) => {
        switch (action) {
            case 'add':
                transactionHook.addEditCategoryRow();
                break;
            case 'remove':
                transactionHook.removeEditCategoryRow(index);
                break;
            case 'update':
                transactionHook.updateEditTransactionCategory(index, field, value);
                break;
            default:
                break;
        }
    };

    return (
        <div>
            {/* Transaction Type Selector */}
            <div className={styles.typeSection}>
                <label className={styles.typeLabel}>Type:</label>
                <select
                    value={transactionHook.editTransactionType}
                    onChange={(e) => {
                        transactionHook.setEditTransactionType(e.target.value);
                        transactionHook.clearEditError('transactionType');
                    }}
                    className={styles.typeSelect}
                >
                    <option value="">Select Type</option>
                    <option value="Expense">Expense</option>
                    <option value="Income">Income</option>
                    <option value="Transfer">Transfer</option>
                    <option value="Adjustment">Adjustment</option>
                </select>
            </div>

            {/* General Error Display */}
            {transactionHook.editErrors.general && (
                <div className={styles.generalError}>
                    ⚠️ {transactionHook.editErrors.general}
                </div>
            )}

            {/* Main Form Fields - using BasicFieldsRow for consistent layout */}
            <div className={styles.formFields}>
                <BasicFieldsRow
                    transactionType={transactionHook.editTransactionType}
                    amount={transactionHook.editAmount}
                    description={transactionHook.editDescription}
                    date={transactionHook.editDate}
                    currentUserKey={transactionHook.editCurrentUserKey}
                    paidDirectly={transactionHook.editPaidDirectly}
                    users={transactionHook.users}
                    budgetStartMonth={budgetStartMonth}
                    errors={transactionHook.editErrors}
                    onFieldChange={handleFieldChange}
                    onCategoryChange={handleCategoryChange}
                    handlePositiveNumberInput={transactionHook.handlePositiveNumberInput}
                    clearError={transactionHook.clearEditError}
                    useTemplateForIncome={false}
                    transactionCategories={transactionHook.editTransactionCategories}
                    singleBankMode={singleBankMode}
                />

                {/* Adjustment-specific form */}
                {transactionHook.editTransactionType === 'Adjustment' && (
                    <AdjustmentFields
                        adjustmentFromCategory={transactionHook.editAdjustmentFromCategory}
                        adjustmentToCategory={transactionHook.editAdjustmentToCategory}
                        categories={transactionHook.categories}
                        errors={transactionHook.editErrors}
                        onFieldChange={handleFieldChange}
                        clearError={transactionHook.clearEditError}
                    />
                )}

                {/* Regular categories section for non-Adjustment transactions */}
                {transactionHook.editTransactionType !== 'Adjustment' && (
                    <CategoryList
                        transactionType={transactionHook.editTransactionType}
                        amount={transactionHook.editAmount}
                        transactionCategories={transactionHook.editTransactionCategories}
                        errors={transactionHook.editErrors}
                        onCategoryChange={handleCategoryChange}
                        clearCategoryError={transactionHook.clearEditCategoryError}
                        getAvailableCategories={transactionHook.getAvailableCategories}
                        getAvailableCategoriesForExpenses={transactionHook.getAvailableCategoriesForExpenses}
                        getAvailableCategoriesForTransfers={transactionHook.getAvailableCategoriesForTransfers}
                        isGrandfatheredCategory={transactionHook.isGrandfatheredCategory}
                        isEdit={true}
                    />
                )}
            </div>
        </div>
    );
}

export default EditTransactionForm;
